import axios from 'axios'
import { uploadToCloudinary } from 'components/FileUploader/fileUploadUtils'
import { compressImage } from 'utils/compressImage'
import { cloudinaryPublicId } from 'utils/cloudinaryPublicId'
import type { CaptureHandlers } from './submitCapture'
import type { SitePayload, NotePayload, PhotoPayload, PhotoResult } from './captureTypes'

/**
 * The real handlers, bound to the API.
 *
 * Separate from `submitCapture` so the funnel stays testable with no network,
 * and so Phase 4 can wrap these — queue when offline, replay on reconnect —
 * without any screen changing.
 *
 * `axios` directly rather than a thunk: a capture is not Redux state. An
 * unlinked site belongs to no evaluation, so there is no slice key to file it
 * under, and the screens hold the capture themselves while it is in flight.
 * Interceptors still apply, so auth and the 401 redirect behave as everywhere
 * else.
 */

const authConfig = (token?: string) =>
	token ? { headers: { Authorization: `Bearer ${token}` } } : undefined

/**
 * @param token The session token. Passed in rather than read from localStorage
 *   here, so a handler is a pure function of its inputs and a test does not
 *   need a fake browser.
 * @param userId Required only for LOCATION notes and images: those endpoints
 *   take `userid` from the client, unlike their site equivalents which derive
 *   it from the session. Not a security boundary either way — the server scopes
 *   on the token — but the field is required and the row is malformed without it.
 */
export const createFieldHandlers = (token?: string, userId?: string): CaptureHandlers => ({
	site: (async (payload: SitePayload) => {
		const { data } = await axios.post('/api/sites', payload, authConfig(token))
		return data
	}) as CaptureHandlers['site'],

	/**
	 * A note on a site or on a location.
	 *
	 * Two endpoints rather than one because they are two collections with
	 * different owners and different scoping — but one handler, because from the
	 * composer's side a note is a note and branching here is cheaper than a
	 * parallel set of handlers that would drift.
	 */
	note: (async (payload: NotePayload) => {
		if (payload.locationId) {
			const { data } = await axios.post(
				'/api/locationNotes',
				{
					userid: userId,
					locationid: payload.locationId,
					projectid: payload.projectId,
					note: payload.html,
					parentid: null,
				},
				authConfig(token),
			)
			return data
		}

		const { data } = await axios.post(
			'/api/sitenotes',
			{ siteid: payload.siteId, note: payload.html, parentid: null },
			authConfig(token),
		)
		return data
	}) as CaptureHandlers['note'],

	/**
	 * One photo: shrink it, upload it, then record it.
	 *
	 * **The binary goes browser → Cloudinary directly** and never touches our
	 * server, which also means it never touches Render's ephemeral disk. The
	 * legacy multer path in `uploadRoutes.js` is deliberately not used.
	 *
	 * **Both writes are required.** A note stores its pictures twice — inline as
	 * `<img>` in the note HTML, and as a row in the image collection so they
	 * appear in the gallery. `utils/noteImages.ts` documents the arrangement.
	 * Miss the row and the Images tab is empty; miss the tag and the note reads
	 * blank. This handler owns the row; the caller puts the returned URL in the
	 * body.
	 *
	 * The image row is written **after** the upload and its id returned, so a
	 * failure at either step fails the whole capture and `submitCapture` keeps
	 * it retryable. A retry re-uploads — Cloudinary tolerates that — but cannot
	 * duplicate a capture that already reached `sent`.
	 */
	photo: (async (payload: PhotoPayload): Promise<PhotoResult> => {
		/*
		 * Two sources, one destination.
		 *
		 * A freshly taken photograph is compressed and uploaded. One pulled from
		 * the field library is ALREADY on Cloudinary, so it skips both: putting
		 * it back through that path would spend a second upload to produce a
		 * second copy of a picture that already exists, and the note would point
		 * at the copy rather than the original.
		 *
		 * Everything after this branch is shared, which is the point of doing it
		 * here rather than in a second handler: the row write, the note's URL and
		 * the retry semantics stay identical whichever way the image arrived.
		 */
		let asset: { url: string; uuid?: string; name?: string; size?: number; type?: string }
		let bytes: number
		let originalBytes: number

		if (payload.libraryImage) {
			const library = payload.libraryImage
			asset = {
				url: library.url,
				uuid: library.uuid,
				name: library.name,
				size: library.size,
				type: library.type,
			}
			// Nothing was transferred, so there is no saving to report.
			bytes = library.size ?? 0
			originalBytes = library.size ?? 0
		} else {
			if (!payload.file) throw new Error('A photo capture needs a file or a library image')

			const compressed = await compressImage(payload.file)
			bytes = compressed.bytes
			originalBytes = compressed.originalBytes

			const folder = payload.siteId
				? `mdt/sites/${payload.siteId}`
				: payload.locationId
					? `mdt/locations/${payload.locationId}`
					: 'mdt/field'

			const uploaded = await uploadToCloudinary(compressed.file, {
				folder,
				resourceType: 'image',
			})
			asset = {
				url: uploaded.secure_url,
				uuid: uploaded.public_id,
				name: uploaded.original_filename,
				size: uploaded.bytes,
				type: uploaded.format,
			}
		}

		const row = {
			/*
				Recovered from the URL when the caller did not supply one.

				`LibraryImageRef` makes every field optional, so a caller can omit
				the Cloudinary id and still compile. That is exactly what happened
				in MYD-462, and the row reached the database with no handle back to
				its own asset. Deriving it here means one forgetful caller cannot
				produce an unidentifiable row, whatever the path.
			*/
			uuid: asset.uuid || cloudinaryPublicId(asset.url) || undefined,
			name: asset.name,
			size: asset.size,
			url: asset.url,
			source: 'cloudinary',
			type: asset.type,
			note: payload.caption || '',
		}

		let imageId: string | undefined
		if (payload.locationId) {
			const { data } = await axios.post(
				'/api/locationImages',
				{
					...row,
					userid: userId,
					locationid: payload.locationId,
					projectid: payload.projectId,
				},
				authConfig(token),
			)
			imageId = data?._id
		} else if (payload.siteId) {
			const { data } = await axios.post(
				'/api/siteimages',
				{ ...row, siteid: payload.siteId },
				authConfig(token),
			)
			imageId = data?._id
		}

		return { url: asset.url, imageId, bytes, originalBytes }
	}) as CaptureHandlers['photo'],
})

export default createFieldHandlers
